import {
  OriginalCurrencySchema as ContractOriginalCurrencySchema,
  PaymentMethodSchema as ContractPaymentMethodSchema,
  SettlementCurrencySchema as ContractSettlementCurrencySchema,
} from "@pwz/contracts";
import { z } from "zod";

const isoDateTime = z.string().min(1);

export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    statusCode: z.number().int(),
    correlationId: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
});

export type ApiErrorBody = z.infer<typeof ApiErrorSchema>;

export const SessionSchema = z.object({
  userId: z.string(),
  role: z.enum(["instructor", "manager", "admin"]),
  instructorId: z.string().nullable(),
  amoUserId: z.union([z.string(), z.number()]).nullable().optional(),
  telegramId: z.union([z.string(), z.number()]).nullable().optional(),
  csrfToken: z.string().nullable(),
  actor: z
    .object({
      id: z.string(),
      role: z.enum(["instructor", "manager", "admin"]),
      instructorId: z.string().optional(),
    })
    .optional(),
});

export type Session = z.infer<typeof SessionSchema>;

export const HandoffSessionSchema = SessionSchema.extend({
  sessionToken: z.string().min(1),
});

export const ManagerCalendarEventSchema = z.object({
  lessonId: z.string(),
  enrollmentId: z.string(),
  lessonOrder: z.number().int(),
  status: z.string(),
  version: z.number().int(),
  startTime: isoDateTime,
  endTime: isoDateTime.nullable(),
  durationMinutes: z.number().int(),
  venueId: z.string().nullable(),
  venueName: z.string().nullable(),
  venueTimezone: z.string().nullable(),
  venueCapacity: z.number().int().nullable(),
  instructorId: z.string().nullable(),
  instructorName: z.string().nullable(),
  clientName: z.string().nullable(),
  clientPhone: z.string().nullable(),
  courseTitle: z.string().nullable(),
  courseCode: z.string().nullable(),
  courseTotalLessons: z.number().int().positive().nullable().optional(),
  coursePriceAmount: z.number().int().nonnegative().nullable().optional(),
  coursePriceCurrency: z.string().nullable().optional(),
  amoLeadId: z.number().int().nullable(),
});

export type ManagerCalendarEvent = z.infer<typeof ManagerCalendarEventSchema>;

export const CalendarQueryResultSchema = z.object({
  role: z.enum(["instructor", "manager", "admin"]),
  rangeStart: z.string(),
  rangeEnd: z.string(),
  events: z.array(ManagerCalendarEventSchema).optional(),
  occupancy: z.unknown().optional(),
});

export type CalendarQueryResult = z.infer<typeof CalendarQueryResultSchema>;

export const AuthMethodsSchema = z.object({
  /** Есть ли хотя бы одна учётка: пока ни одной, форму пароля показывать нечему. */
  password: z.boolean(),
  telegram: z.object({ available: z.boolean(), botUsername: z.string().nullable() }),
  amoWidget: z.boolean(),
});

export type AuthMethods = z.infer<typeof AuthMethodsSchema>;

export const PasswordLoginResultSchema = z.object({
  role: z.enum(["manager", "admin"]),
  userId: z.string(),
  displayName: z.string(),
  expiresAt: z.string(),
  csrfToken: z.string().nullable(),
});

export type PasswordLoginResult = z.infer<typeof PasswordLoginResultSchema>;

export const AppUserSchema = z.object({
  id: z.string(),
  login: z.string(),
  displayName: z.string(),
  role: z.enum(["manager", "admin"]),
  amoUserId: z.number().int().nullable(),
  isActive: z.boolean(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type AppUser = z.infer<typeof AppUserSchema>;

export const AppUsersResponseSchema = z.object({ items: z.array(AppUserSchema) });

export const LogoutResultSchema = z.object({
  ok: z.boolean(),
  revoked: z.enum(["one", "all", "none"]).optional(),
});

export type LogoutResult = z.infer<typeof LogoutResultSchema>;

export const AmoAccountSchema = z.object({
  configured: z.boolean(),
  baseUrl: z.string().nullable(),
  /** Конфиг держит id аккаунта строкой — принимаем обе формы, как и в схеме сессии. */
  accountId: z.union([z.string(), z.number()]).nullable(),
});

export type AmoAccount = z.infer<typeof AmoAccountSchema>;

/**
 * Локация (каталог amo 6819): остров со своей валютой и своим часовым поясом. Календарь
 * менеджера делится по ней надвое — Бали и Пхукет не смешиваются ни временем, ни инструкторами.
 */
export const LocationSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().nullable().optional(),
  currency: z.string(),
  timezone: z.string(),
  isActive: z.boolean().optional(),
});

export type Location = z.infer<typeof LocationSchema>;

export const LocationsResponseSchema = z.object({
  items: z.array(LocationSchema),
});

export const VenueSchema = z.object({
  id: z.string(),
  name: z.string(),
  capacity: z.number().int(),
  timezone: z.string(),
  /** Элемент каталога локаций: по нему площадка попадает в свой календарь. */
  locationId: z.string().nullable().optional(),
  isActive: z.boolean().optional(),
  amoEnumValue: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type Venue = z.infer<typeof VenueSchema>;

export const VenuesResponseSchema = z.object({
  items: z.array(VenueSchema),
});

export const InstructorSchema = z.object({
  id: z.string(),
  name: z.string(),
  isActive: z.boolean().optional(),
  defaultPostLessonBufferMinutes: z.number().int().optional(),
  locationId: z.string().nullable().optional(),
  locationName: z.string().nullable().optional(),
  timezone: z.string().optional(),
});

export type Instructor = z.infer<typeof InstructorSchema>;

export const InstructorsResponseSchema = z.object({
  items: z.array(InstructorSchema),
});

export const DealChainPartSchema = z.enum(["location", "vehicle", "course"]);

export const DealContextSchema = z.object({
  leadId: z.number().int(),
  /** Lead name from amo — deals are named after the client, the header shows it. */
  dealName: z.string().nullable().default(null),
  instructorId: z.string().nullable(),
  instructorName: z.string().nullable(),
  instructorTimezone: z.string().nullable(),
  locationName: z.string().nullable(),
  venueId: z.string().nullable(),
  // Catalog chain read from the amo `Площадка+Курс` field.
  locationElementId: z.number().int().nullable().default(null),
  vehicleElementId: z.number().int().nullable().default(null),
  vehicleName: z.string().nullable().default(null),
  courseElementId: z.number().int().nullable().default(null),
  courseName: z.string().nullable().default(null),
  /** Courses linked to the deal as products of catalog 7631 — several is a normal sale (§3.2). */
  courses: z
    .array(
      z.object({
        elementId: z.number().int(),
        name: z.string().nullable(),
        quantity: z.number(),
        lessonCount: z.number().int().nullable(),
        minutesPerLesson: z.number().int().nullable(),
        validityDays: z.number().int().nullable(),
        priceUsd: z.number().nullable(),
        priceIdr: z.number().nullable(),
        priceThb: z.number().nullable(),
        priceLocal: z.number().nullable(),
        vehicleElementId: z.number().int().nullable(),
        inCatalogCache: z.boolean(),
        /** Доп. опция («Права»): цена есть, уроков нет — плана по ней не бывает. */
        isAddon: z.boolean().default(false),
      }),
    )
    .default([]),
  missingDealData: z.array(DealChainPartSchema).default([]),
});

export type DealChainPart = z.infer<typeof DealChainPartSchema>;

export type DealContext = z.infer<typeof DealContextSchema>;

export const ScheduledLessonRecordSchema = z.object({
  id: z.string(),
  enrollmentId: z.string(),
  lessonOrder: z.number().int(),
  instructorId: z.string().nullable(),
  venueId: z.string().nullable(),
  status: z.string(),
  startTime: isoDateTime.nullable(),
  endTime: isoDateTime.nullable(),
  durationMinutes: z.number().int(),
  postLessonBufferMinutes: z.number().int().nullable(),
  capacityOverrideReason: z.string().nullable(),
  version: z.number().int(),
  updatedAt: z.string(),
});

export type ScheduledLessonRecord = z.infer<typeof ScheduledLessonRecordSchema>;

export const LessonPlanRecordSchema = z.object({
  id: z.string(),
  enrollmentId: z.string(),
  lessonOrder: z.number().int().positive(),
  instructorId: z.string().nullable(),
  status: z.string(),
  startTime: isoDateTime.nullable(),
  durationMinutes: z.number().int().positive(),
  locationType: z.string(),
  version: z.number().int().positive(),
  templateSnapshot: z.unknown(),
  contentSnapshot: z.unknown(),
});

export type LessonPlanRecord = z.infer<typeof LessonPlanRecordSchema>;

export const ScheduleMutationResultSchema = z.object({
  lesson: ScheduledLessonRecordSchema,
  reminderJobId: z.string().nullable(),
  invalidatedReminderJobIds: z.array(z.string()),
  replayed: z.boolean(),
});

export type ScheduleMutationResult = z.infer<typeof ScheduleMutationResultSchema>;

/**
 * Способы, которыми инструктор может принять деньги: словарь общий с сервером. Старые «Карта»
 * и «Перевод внутри страны» остаются читаемыми — предписания по ним записаны в старых сделках.
 */
export const SettlementMethodSchema = z.enum([
  "cash",
  "transfer",
  "crypto",
  "card",
  "local_transfer",
]);
export type SettlementMethod = z.infer<typeof SettlementMethodSchema>;

export const EnrollmentSchema = z
  .object({
    id: z.string(),
    status: z.string().optional(),
    state: z.string().optional(),
    primaryInstructorId: z.string().optional().nullable(),
    amoLeadId: z.number().int().optional().nullable(),
    amoCourseElementId: z.number().int().optional(),
    settlementMethod: SettlementMethodSchema.nullable().optional(),
    settlementNote: z.string().nullable().optional(),
  })
  .passthrough();

export type Enrollment = z.infer<typeof EnrollmentSchema>;

export const CreateDealEnrollmentResultSchema = z.object({
  created: z.boolean(),
  enrollment: EnrollmentSchema,
  lessons: z.array(LessonPlanRecordSchema),
});

export type CreateDealEnrollmentResult = z.infer<typeof CreateDealEnrollmentResultSchema>;

export const EnrollmentsByLeadSchema = z.object({
  items: z.array(EnrollmentSchema),
});

export const LessonsListSchema = z.object({
  items: z.array(ScheduledLessonRecordSchema),
});

/** Ответ на просьбу передать запись менеджеру. */
export const HandoffNextResultSchema = z.object({
  handedOff: z.boolean(),
  lessonId: z.string().nullable(),
  replayed: z.boolean(),
});

export type HandoffNextResult = z.infer<typeof HandoffNextResultSchema>;

export const UnscheduledLessonSchema = z.object({
  lessonId: z.string(),
  enrollmentId: z.string(),
  lessonOrder: z.number().int(),
  status: z.string(),
  version: z.number().int(),
  durationMinutes: z.number().int(),
  instructorId: z.string().nullable(),
  clientName: z.string().nullable(),
  courseTitle: z.string().nullable(),
  courseCode: z.string().nullable(),
  courseTotalLessons: z.number().int().positive().nullable().optional(),
  coursePriceAmount: z.number().int().nonnegative().nullable().optional(),
  coursePriceCurrency: z.string().nullable().optional(),
  amoLeadId: z.number().int().nullable(),
  enrollmentStatus: z.string(),
});

export type UnscheduledLesson = z.infer<typeof UnscheduledLessonSchema>;

export const UnscheduledLessonsResponseSchema = z.object({
  items: z.array(UnscheduledLessonSchema),
});

export const OccupancyBlockSchema = z.object({
  startTime: isoDateTime,
  endTime: isoDateTime.nullable(),
  venueId: z.string().nullable(),
  venueName: z.string().nullable(),
  instructorId: z.string().nullable(),
  instructorName: z.string().nullable(),
  occupiedSlots: z.number().int(),
  capacity: z.number().int().nullable(),
});

export type OccupancyBlock = z.infer<typeof OccupancyBlockSchema>;

export const OccupancyResponseSchema = z.object({
  blocks: z.array(OccupancyBlockSchema),
});

export const PaymentSnapshotSchema = z.object({
  status: z.string().nullable(),
  amount: z.number().int().nullable(),
  currency: z.string().nullable(),
  notes: z.string().nullable(),
});

export type PaymentSnapshot = z.infer<typeof PaymentSnapshotSchema>;

export const OwnLessonDetailSchema = z.object({
  lessonId: z.string(),
  enrollmentId: z.string(),
  /** Сделка в amo: по ней форма отчёта читает долг из журнала (§7.1). */
  amoLeadId: z.number().int(),
  lessonOrder: z.number().int(),
  status: z.string(),
  version: z.number().int(),
  startTime: isoDateTime,
  endTime: isoDateTime.nullable(),
  durationMinutes: z.number().int(),
  venueId: z.string().nullable(),
  venueName: z.string().nullable(),
  venueTimezone: z.string().nullable(),
  instructorId: z.string(),
  instructorName: z.string(),
  clientName: z.string(),
  clientPhone: z.string().nullable(),
  clientTelegramUsername: z.string().nullable(),
  clientAvatarUrl: z.string().nullable(),
  clientMessengerDeepLink: z.string().nullable(),
  courseTitle: z.string(),
  courseCode: z.string().nullable(),
  experience: z.string().nullable(),
  paymentStatus: z.string().nullable(),
  paymentSnapshot: PaymentSnapshotSchema.nullable(),
  /** Предписание менеджера: как принять деньги за этот курс. `null` — решает инструктор. */
  settlementMethod: SettlementMethodSchema.nullable(),
  settlementNote: z.string().nullable(),
  isPrimaryInstructor: z.boolean(),
  totalLessons: z.number().int().nullable(),
});

export type OwnLessonDetail = z.infer<typeof OwnLessonDetailSchema>;

export const InstructorScheduleViewSchema = z.object({
  instructorId: z.string(),
  rangeStart: z.string(),
  rangeEnd: z.string(),
  ownLessons: z.array(OwnLessonDetailSchema),
  busyBlocks: z.array(OccupancyBlockSchema),
});

export type InstructorScheduleView = z.infer<typeof InstructorScheduleViewSchema>;

export const AuthBootstrapSchema = z.object({
  role: z.enum(["instructor", "manager", "admin"]),
  userId: z.string(),
  instructorId: z.string().optional().nullable(),
  expiresAt: z.string(),
  csrfToken: z.string().nullable(),
});

export type AuthBootstrap = z.infer<typeof AuthBootstrapSchema>;

export const OidcStartSchema = z.object({
  authorizationUrl: z.string().min(1),
  state: z.string().min(1).optional(),
});

export type OidcStart = z.infer<typeof OidcStartSchema>;

export const AmoOAuthStartSchema = z.object({
  authorizationUrl: z.string().min(1),
  state: z.string().min(1),
});

export type AmoOAuthStart = z.infer<typeof AmoOAuthStartSchema>;

export const AmoStatusSchema = z.object({
  configured: z.boolean(),
  connected: z.boolean(),
  hasRefreshToken: z.boolean(),
  expiresAt: z.string().nullable(),
  redirectUri: z.string().nullable(),
  accountId: z.string().nullable(),
});

export type AmoStatus = z.infer<typeof AmoStatusSchema>;

export const PaymentMethodSchema = z.enum([
  "cash",
  "bank_transfer",
  "card",
  "usdt",
  "other",
  "already_paid",
]);

export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

export const PaymentReportSchema = z.object({
  id: z.string(),
  status: z.string(),
  enrollmentId: z.string().nullable(),
  leadId: z.number().int().nullable().optional(),
  amount: z.number().int(),
  currency: z.string(),
  notes: z.string().nullable().optional(),
  reviewNotes: z.string().nullable().optional(),
  reviewerId: z.string().nullable().optional(),
  reviewedAt: z.string().nullable().optional(),
  commercialSnapshot: z.unknown().nullable().optional(),
  evidenceUrl: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  replayed: z.boolean().optional(),
});

export type PaymentReport = z.infer<typeof PaymentReportSchema>;

export const PaymentReportsResponseSchema = z.object({
  items: z.array(PaymentReportSchema),
});

export const EnrollmentsListSchema = z.object({
  items: z.array(EnrollmentSchema),
});

export const OperationalLogItemSchema = z.object({
  id: z.string(),
  timestamp: z.string(),
  severity: z.string(),
  subsystem: z.string(),
  eventName: z.string(),
  correlationId: z.string(),
  retryStatus: z.string().nullable().optional(),
  errorSummary: z.string().nullable().optional(),
  payload: z.record(z.string(), z.unknown()).optional(),
  actorId: z.string().nullable().optional(),
});

export type OperationalLogItem = z.infer<typeof OperationalLogItemSchema>;

export const OperationalLogsResponseSchema = z.object({
  items: z.array(OperationalLogItemSchema),
});

export const AdminCatalogLocationSchema = z.object({
  elementId: z.number().int(),
  name: z.string(),
  code: z.string().nullable(),
  currency: z.string(),
  timezone: z.string(),
  isActive: z.boolean(),
});

export type AdminCatalogLocation = z.infer<typeof AdminCatalogLocationSchema>;

export const AdminCatalogCourseSchema = z.object({
  elementId: z.number().int(),
  name: z.string(),
  isActive: z.boolean(),
  sku: z.string().nullable(),
  vehicleElementId: z.number().int().nullable(),
  lessonCount: z.number().int(),
  minutesPerLesson: z.number().int(),
  minutesTotal: z.number().int(),
  validityDays: z.number().int().nullable(),
  priceUsd: z.number().nullable(),
  priceIdr: z.number().nullable(),
  priceThb: z.number().nullable(),
});

export type AdminCatalogCourse = z.infer<typeof AdminCatalogCourseSchema>;

export const AdminCatalogInstructorSchema = z.object({
  elementId: z.number().int(),
  name: z.string(),
  isActive: z.boolean(),
  code: z.string().nullable(),
  telegramUsername: z.string().nullable(),
  telegramId: z.string().nullable(),
  locationElementId: z.number().int().nullable(),
  /**
   * Сервер отдаёт вид техники, а не ссылку на элемент каталога:
   * directory.toInstructor читает поле как «scooter» | «motorcycle».
   */
  vehicles: z.array(z.string()),
});

export type AdminCatalogInstructor = z.infer<typeof AdminCatalogInstructorSchema>;

export const AdminCatalogVenueSchema = z.object({
  elementId: z.number().int(),
  name: z.string(),
  isActive: z.boolean(),
  code: z.string().nullable(),
  locationElementId: z.number().int().nullable(),
  district: z.string().nullable(),
  address: z.string().nullable(),
  mapUrl: z.string().nullable(),
  capacity: z.number().int(),
  /** Как и у инструктора — вид техники строкой, а не elementId. */
  vehicles: z.array(z.string()),
  postLessonBufferMinutes: z.number().int(),
});

export type AdminCatalogVenue = z.infer<typeof AdminCatalogVenueSchema>;

export const AdminCatalogVehicleSchema = z.object({
  elementId: z.number().int(),
  name: z.string(),
  isActive: z.boolean(),
});

export type AdminCatalogVehicle = z.infer<typeof AdminCatalogVehicleSchema>;

/** How a catalog field is sourced — drives whether the admin UI lets you edit it. */
export const AdminFieldOwnershipSchema = z.enum(["amo", "two_way", "local"]);

export type AdminFieldOwnership = z.infer<typeof AdminFieldOwnershipSchema>;

export const AdminFieldOwnershipMapSchema = z.object({
  courses: z.record(z.string(), AdminFieldOwnershipSchema),
  instructors: z.record(z.string(), AdminFieldOwnershipSchema),
  venues: z.record(z.string(), AdminFieldOwnershipSchema),
});

export type AdminFieldOwnershipMap = z.infer<typeof AdminFieldOwnershipMapSchema>;

export const EditableAdminCatalogSchema = z.enum(["courses", "instructors", "venues"]);

export type EditableAdminCatalog = z.infer<typeof EditableAdminCatalogSchema>;

/**
 * amoCRM is the source of truth for these catalogs; the admin surface caches the last sync
 * and lets `two_way`/`local` fields (per fieldOwnership) be edited back through the server.
 */
export const AdminCatalogsResponseSchema = z.object({
  syncedAt: z.string().nullable(),
  fieldOwnership: AdminFieldOwnershipMapSchema,
  locations: z.array(AdminCatalogLocationSchema),
  courses: z.array(AdminCatalogCourseSchema),
  venues: z.array(AdminCatalogVenueSchema),
  instructors: z.array(AdminCatalogInstructorSchema),
  vehicles: z.array(AdminCatalogVehicleSchema),
});

export type AdminCatalogs = z.infer<typeof AdminCatalogsResponseSchema>;

export const CatalogFieldValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);

export type CatalogFieldValue = z.infer<typeof CatalogFieldValueSchema>;

export const UpdateCatalogElementResponseSchema = z.object({
  catalog: EditableAdminCatalogSchema,
  elementId: z.number().int(),
  element: z.record(z.string(), z.unknown()),
});

export type UpdateCatalogElementResponse = z.infer<typeof UpdateCatalogElementResponseSchema>;

export const SyncCatalogsResponseSchema = z.object({
  synced: z.literal(true),
  syncedAt: z.string(),
  counts: z.record(z.string(), z.number().int()).optional(),
});

export type SyncCatalogsResponse = z.infer<typeof SyncCatalogsResponseSchema>;

export const InstructorMappingSchema = z.object({
  id: z.string(),
  name: z.string(),
  amoCatalogId: z.number().int().nullable().optional(),
  code: z.string().nullable().optional(),
  telegramBound: z.boolean().optional(),
  telegramId: z.string().nullable().optional(),
  /** Handle Telegram reported when the binding happened. */
  telegramUsername: z.string().nullable().optional(),
  /** Handle the manager typed into the amo card — empty means nothing to match on. */
  amoUsername: z.string().nullable().optional(),
  /** Telegram id mirrored back into the amo card: proof the bot found this person. */
  amoTelegramId: z.string().nullable().optional(),
  locationName: z.string().nullable().optional(),
  timezone: z.string().optional(),
  isActive: z.boolean(),
  defaultPostLessonBufferMinutes: z.number().int().optional(),
  updatedAt: z.string().optional(),
});

export type InstructorMapping = z.infer<typeof InstructorMappingSchema>;

export const InstructorMappingsResponseSchema = z.object({
  items: z.array(InstructorMappingSchema),
});

export const VenueMappingResultSchema = z
  .object({
    mapped: z.number().int().optional(),
    created: z.number().int().optional(),
    updated: z.number().int().optional(),
    skipped: z.number().int().optional(),
  })
  .passthrough();

/** Payment-method markups of the finance block; missing method = 0 %. */
/**
 * План передачи сделки другому инструктору (форс-мажор).
 *
 * Это ответ на вопрос «что произойдёт», а не «что произошло»: менеджер читает его до
 * подтверждения. Каждый урок несёт свой вердикт — уже проведён, поедет как есть, потеряет
 * время, — потому что решение принимается по этому списку, а не по числу уроков.
 */
export const DealTransferVerdictSchema = z.enum(["held", "unscheduled", "free", "busy"]);
export type DealTransferVerdict = z.infer<typeof DealTransferVerdictSchema>;

export const DealTransferLessonSchema = z.object({
  lessonId: z.string(),
  enrollmentId: z.string(),
  lessonOrder: z.number().int(),
  status: z.string(),
  version: z.number().int(),
  startTime: z.string().nullable(),
  durationMinutes: z.number().int(),
  venueId: z.string().nullable(),
  venueName: z.string().nullable(),
  venueTimezone: z.string().nullable(),
  instructorId: z.string().nullable(),
  instructorName: z.string().nullable(),
  verdict: DealTransferVerdictSchema,
  conflictingLessonId: z.string().nullable(),
  keepsTime: z.boolean(),
});

export type DealTransferLesson = z.infer<typeof DealTransferLessonSchema>;

export const DealTransferPlanSchema = z.object({
  amoLeadId: z.number().int(),
  toInstructorId: z.string(),
  toInstructorName: z.string().nullable(),
  fromInstructors: z.array(z.object({ id: z.string(), name: z.string().nullable() })),
  enrollmentIds: z.array(z.string()),
  lessons: z.array(DealTransferLessonSchema),
  movingCount: z.number().int(),
  heldCount: z.number().int(),
  conflictCount: z.number().int(),
  /** Есть только в ответе на саму передачу: что в итоге переехало и что потеряло время. */
  movedLessonIds: z.array(z.string()).optional(),
  clearedLessonIds: z.array(z.string()).optional(),
});

export type DealTransferPlan = z.infer<typeof DealTransferPlanSchema>;

export const FinanceSettingsSchema = z.object({
  currencySurcharges: z.record(z.string(), z.number()),
  isDefault: z.boolean(),
  updatedBy: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

export type FinanceSettings = z.infer<typeof FinanceSettingsSchema>;

export const FxQuoteSchema = z.object({
  base: z.string(),
  quote: z.string(),
  rate: z.number(),
  source: z.string(),
  fetchedAt: z.string(),
  stale: z.boolean(),
});

export type FxQuote = z.infer<typeof FxQuoteSchema>;

export const FxHistoryEntrySchema = z.object({
  rate: z.number(),
  source: z.string(),
  fetchedAt: z.string(),
});

export type FxHistoryEntry = z.infer<typeof FxHistoryEntrySchema>;

/** Ответ загрузки чека: ссылка на файл в amo Drive для поля «Чек оплаты». */
export const LedgerReceiptSchema = z.object({
  fileId: z.string(),
  url: z.string().nullable(),
});

export const FxHistoryResponseSchema = z.object({
  items: z.array(FxHistoryEntrySchema),
});

/**
 * Валюты и способы оплаты приходят из `@pwz/contracts` — того же словаря, по которому сервер
 * разбирает запрос. Своя копия здесь молча расходилась с серверной.
 */
const SettlementCurrencySchema = ContractSettlementCurrencySchema;
export const OriginalCurrencySchema = ContractOriginalCurrencySchema;
/** Способы журнала (§3.3) — не путать с легаси `PaymentMethodSchema` выше. */
export const LedgerMethodSchema = ContractPaymentMethodSchema;

/** One receipt as the journal keeps it (§6): what was handed over, and what it became locally. */
export const LedgerPaymentSchema = z.object({
  id: z.string(),
  leadId: z.number().int(),
  status: z.enum(["pending", "confirmed", "rejected"]),
  operationType: z.enum(["deposit", "refund"]),
  amountOriginalMinor: z.number().int(),
  currencyOriginal: OriginalCurrencySchema,
  rateToLocal: z.number(),
  effectiveRateToLocal: z.number(),
  surchargePercent: z.number(),
  currency: SettlementCurrencySchema,
  amountLocal: z.number().int(),
  usdtRate: z.number(),
  amountUsdtCents: z.number().int(),
  rateStale: z.boolean(),
  method: LedgerMethodSchema,
  acceptorRole: z.enum(["instructor", "manager"]),
  acceptorInstructorId: z.string().nullable(),
  venueElementId: z.number().int().nullable(),
  acceptedAt: z.string().nullable(),
  allocation: z.array(
    z.object({
      courseElementId: z.number().int(),
      amountLocal: z.number().int(),
      amountUsdtCents: z.number().int(),
    }),
  ),
  amoElementId: z.number().int().nullable(),
  reviewerId: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  reviewNotes: z.string().nullable(),
  /** Чек в amo Drive — то, чем подтверждено поступление; `null`, если платили наличными. */
  receiptUrl: z.string().nullable(),
  comment: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  replayed: z.boolean().optional(),
});

export type LedgerPayment = z.infer<typeof LedgerPaymentSchema>;

export const LedgerPaymentsResponseSchema = z.object({
  items: z.array(LedgerPaymentSchema),
});
export type LedgerMethod = z.infer<typeof LedgerMethodSchema>;
export type OriginalCurrency = z.infer<typeof OriginalCurrencySchema>;

/** The frozen contract snapshot: card prices as they were when the deal was signed (§6). */
export const LedgerContractSchema = z.object({
  id: z.string(),
  leadId: z.number().int(),
  currency: SettlementCurrencySchema,
  usdtRate: z.number(),
  lines: z.array(
    z.object({
      courseElementId: z.number().int(),
      quantity: z.number().int(),
      priceLocal: z.number().int(),
      priceUsdt: z.number().nullable(),
      enrollmentId: z.string().optional(),
    }),
  ),
  supersedesContractId: z.string().nullable(),
  reason: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  charge: z.object({
    currency: SettlementCurrencySchema,
    grossLocal: z.number().int(),
    discountPercent: z.number(),
    chargedLocal: z.number().int(),
    chargedUsdtCents: z.number().int(),
    budgetUsdt: z.number(),
    cardUsdt: z.number(),
    lines: z.array(
      z.object({
        courseElementId: z.number().int(),
        chargedLocal: z.number().int(),
        enrollmentId: z.string().optional(),
      }),
    ),
  }),
  replayed: z.boolean().optional(),
});

export type LedgerContract = z.infer<typeof LedgerContractSchema>;

export const DealLedgerSchema = z.object({
  leadId: z.number().int(),
  currency: SettlementCurrencySchema.nullable(),
  contract: LedgerContractSchema.nullable(),
  balance: z.object({
    chargedLocal: z.number().int().nullable(),
    paidLocal: z.number().int(),
    paidUsdtCents: z.number().int(),
    pendingLocal: z.number().int(),
    refundedLocal: z.number().int(),
    debtLocal: z.number().int().nullable(),
    overpaidLocal: z.number().int(),
    paymentStatus: z.enum(["not_paid", "advance", "partial", "paid", "overpaid", "unknown"]),
  }),
  payments: z.array(LedgerPaymentSchema),
});

export type DealLedger = z.infer<typeof DealLedgerSchema>;

export const BackupMetadataSchema = z.object({
  lastExport: z
    .object({
      auditId: z.string(),
      checksumSha256: z.string().nullable(),
      exportedAt: z.string(),
      status: z.string(),
      triggeredBy: z.string(),
    })
    .nullable(),
  pendingUploads: z.number().int(),
  pendingUploadIds: z.array(z.string()).optional(),
  scheduled: z.literal(false),
  automaticOffload: z.literal(false),
  secretsExcluded: z.boolean().optional(),
  envExcluded: z.boolean().optional(),
});

export type BackupMetadata = z.infer<typeof BackupMetadataSchema>;

export const BackupDownloadResultSchema = z.object({
  blob: z.instanceof(Blob),
  filename: z.string(),
  checksumSha256: z.string(),
  archiveChecksumSha256: z.string(),
  auditId: z.string(),
  pendingUploads: z.number().int().optional(),
});

export type BackupDownloadResult = z.infer<typeof BackupDownloadResultSchema>;

export const InstructorNoteResultSchema = z
  .object({
    id: z.string().optional(),
    lessonId: z.string().optional(),
    text: z.string().optional(),
    replayed: z.boolean().optional(),
  })
  .passthrough();

export const NextUnscheduledLessonSchema = z.object({
  lessonId: z.string(),
  enrollmentId: z.string(),
  lessonOrder: z.number().int(),
  status: z.string(),
  version: z.number().int(),
  durationMinutes: z.number().int(),
  courseTitle: z.string().nullable(),
  courseCode: z.string().nullable(),
  instructorId: z.string(),
});

export type NextUnscheduledLesson = z.infer<typeof NextUnscheduledLessonSchema>;

export const NextUnscheduledResponseSchema = z.object({
  item: NextUnscheduledLessonSchema.nullable(),
});

/** Деньги по валютам: в валюте приёма (минорные единицы), в валюте расчёта и свод в USDT. */
export const MoneyBagSchema = z.object({
  byOriginal: z.record(z.string(), z.number()),
  bySettlement: z.record(z.string(), z.number()),
  usdtCents: z.number(),
  receipts: z.number().int(),
});

export type MoneyBag = z.infer<typeof MoneyBagSchema>;

export const TakingsResponseSchema = z.object({
  from: z.string(),
  to: z.string(),
  instructors: z.array(
    z.object({
      instructorId: z.string(),
      name: z.string().nullable(),
      brought: MoneyBagSchema,
      pending: MoneyBagSchema,
      cashTaken: MoneyBagSchema,
    }),
  ),
  managers: z.array(
    z.object({
      amoUserId: z.number().int(),
      name: z.string().nullable(),
      brought: MoneyBagSchema,
      pending: MoneyBagSchema,
    }),
  ),
  unattributed: MoneyBagSchema.nullable(),
});

export type Takings = z.infer<typeof TakingsResponseSchema>;

/** Ученик инструктора — сделка, которую он ведёт. */
export const InstructorStudentSchema = z.object({
  enrollmentId: z.string(),
  amoLeadId: z.number().int(),
  clientName: z.string().nullable(),
  clientPhone: z.string().nullable(),
  clientAvatarUrl: z.string().nullable().default(null),
  courseName: z.string().nullable(),
  status: z.string(),
  lessonsDone: z.number().int(),
  lessonsTotal: z.number().int(),
  nextLesson: z
    .object({
      lessonId: z.string(),
      lessonOrder: z.number().int(),
      startTime: z.string(),
      venueName: z.string().nullable(),
      venueTimezone: z.string().nullable(),
    })
    .nullable(),
  nextToBookOrder: z.number().int().nullable(),
});

export type InstructorStudent = z.infer<typeof InstructorStudentSchema>;

export const InstructorStudentsResponseSchema = z.object({
  items: z.array(InstructorStudentSchema),
});
